/*
 * Copyright 2026, Airspan Networks
 * All Rights Reserved.
 */

import React, { useCallback, useEffect } from "react";
import { useInventoryData } from "./hooks";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useStoreDispatch } from "../../../store/hooks";
import { setPaginationModel } from "../../../store/helpers/helperReducers";
import { GridToolbarContainer } from "@mui/x-data-grid";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";
import InventoryFilter from "../../../components/Nodes/InventoryFilter/InventoryFilter";
import { useInventoryFilter } from "../../../components/Nodes/InventoryFilter/hooks";
import ActiveFilterChips from "../../../components/Shared/Filter/ActiveFilterChips";
import { useLocation } from "react-router-dom";
import { resetKeywordForScope } from "../../../store/search/reducer";
import { searchKeywordByScopeSelector } from "../../../store/search/selector";
import { initialState } from "../../../store/helpers/helperInitialState";
import { resetInventoryFilters, setSelectedView, setRegionFilterIds as setInventoryRegionFilterIds } from "../../../store/filters/Inventory/reducer";
import {
  setSelectedDeviceStatus,
  setMaintenanceMode,
  setSelectedDeviceKpiStatus,
  setSelectedDeviceConfigStatus,
  setSelectedDeviceSiteGroup,
  setRegionFilterIds as setNavRegionFilterIds,
} from "../../../store/filters/Nodes/reducer";
import { inventoryViewSelector, inventoryRegionFilterIdsSelector } from "../../../store/filters/Inventory/selectors";
import { selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { deviceRegionFilterIdsSelector } from "../../../store/filters/Nodes/selectors";
import DeviceDrawer from "../../../components/Drawers/DeviceDrawer";
import { selectedDeviceSelector, isOpenDeviceDrawerSelector } from "../../../store/api/devices/selectors";

const InventoryDisplay = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const { data, totalItems, isLoading, InventoryColumns, exportHandler, exportStatus, setInventoryTableSort } = useInventoryData();
  const noOfRows = totalItems;
  const view = useSelector(inventoryViewSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const isOpenDeviceDrawer = useSelector(isOpenDeviceDrawerSelector);
  const { filters: inventoryFilters } = useInventoryFilter();
  const inventorySearchKeyword = useSelector(searchKeywordByScopeSelector("inventory"));
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);
  const regionFilterIds = useSelector(inventoryRegionFilterIdsSelector);
  const navRegionFilterIds = useSelector(deviceRegionFilterIdsSelector);
  const handleClearSearch = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: "inventory" }));
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    dispatch(resetInventoryFilters());
    dispatch(resetKeywordForScope({ scope: "inventory" }));
    dispatch(setPaginationModel({ inventory: initialState.pagination }));
    dispatch(setSelectedDeviceStatus(""));
    dispatch(setMaintenanceMode(""));
    dispatch(setSelectedDeviceKpiStatus(""));
    dispatch(setSelectedDeviceConfigStatus(""));
    dispatch(setSelectedDeviceSiteGroup(""));
    dispatch(setNavRegionFilterIds([]));
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname.endsWith("/inventory")) {
      dispatch(resetInventoryFilters());
      dispatch(resetKeywordForScope({ scope: "inventory" }));
      dispatch(setPaginationModel({ inventory: initialState.pagination }));
    }
  }, [dispatch, location.pathname]);

  // When the top-level geo-region selection changes to a restricted set, clear any
  // stale region filter IDs that are no longer valid within that restricted set.
  useEffect(() => {
    if (!selectedGeoRegionIds || selectedGeoRegionIds.length === 0) return;

    if (regionFilterIds.length > 0) {
      const validIds = regionFilterIds.filter((id: string) => selectedGeoRegionIds.includes(id));
      if (validIds.length !== regionFilterIds.length) {
        dispatch(setInventoryRegionFilterIds(validIds));
      }
    }

    if (navRegionFilterIds.length > 0) {
      const validNavIds = navRegionFilterIds.filter((id: string) => selectedGeoRegionIds.includes(id));
      if (validNavIds.length !== navRegionFilterIds.length) {
        dispatch(setNavRegionFilterIds(validNavIds));
      }
    }
  }, [selectedGeoRegionIds]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div className="flex flex-col w-full overflow-y-auto text-foreground">
      <Tabs
        value={view}
        onValueChange={(newView) => {
          dispatch(setSelectedView(newView as "radionodes" | "cells"));
          dispatch(resetInventoryFilters());
          dispatch(resetKeywordForScope({ scope: "inventory" }));
        }}
        className="w-full"
      >
        <TabsList className="inline-flex h-9 w-fit shrink-0 self-start items-stretch gap-0.5 rounded-full border border-border bg-transparent dark:bg-muted p-0.5 text-muted-foreground">
          <TabsTrigger
            value="radionodes"
            className="rounded-full !bg-transparent px-4 h-full inline-flex items-center justify-center text-sm font-medium text-muted-foreground !shadow-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:!bg-muted dark:data-[state=active]:!bg-card data-[state=active]:text-foreground data-[state=active]:!shadow-sm"
          >
            Radio nodes
          </TabsTrigger>
          <TabsTrigger
            value="cells"
            className="rounded-full !bg-transparent px-4 h-full inline-flex items-center justify-center text-sm font-medium text-muted-foreground !shadow-none transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:!bg-muted dark:data-[state=active]:!bg-card data-[state=active]:text-foreground data-[state=active]:!shadow-sm"
          >
            Cells
          </TabsTrigger>
        </TabsList>
        <div className="space-y-2 mt-8">
          <InventoryFilter />
          <div className="flex justify-between items-end mb-0 mt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{`Showing ${noOfRows} results`}</span>
              <ActiveFilterChips
                filters={inventoryFilters}
                searchKeyword={inventorySearchKeyword}
                onClearSearch={handleClearSearch}
                onClearAll={handleClearAll}
                dispatch={dispatch}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-6 w-8 shrink-0 relative -top-1"
              disabled={exportStatus.isLoading}
              onClick={exportHandler}
              aria-label="Export CSV"
            >
              {exportStatus.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
            </Button>
          </div>
          <div  style={{marginTop: "2px"}}>
            <CustomDataGrid
              tableId="inventory"
              rows={data}
              columns={InventoryColumns}
              rowCount={noOfRows}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              getRowId={(row: any) => row.hostname}
              onSortModelChange={(sortModel) => {
                if (sortModel.length > 0) {
                  setInventoryTableSort(`${sortModel[0].field},${sortModel[0].sort}`);
                }
              }}
              fillContainer
            />
          </div>
        </div>
      </Tabs>
      {isOpenDeviceDrawer && !!selectedDevice?.nodeType && (
        <DeviceDrawer data={selectedDevice} closedButtonClicked={() => {}} />
      )}
    </div>
  );
};

export default InventoryDisplay;
